import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useStore } from 'zustand';
import { AlertTriangle, Check, FileImage, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ApiError, getStudyRef, lookupStudy } from '../../features/collections/api';
import type { MetadataDraft } from '../../features/upload/sidecar';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { Field, MetadataFields, Note } from '../upload/MetadataForm';
import { wireOf } from '../upload/details';
import {
  CASE,
  CASE_FIELDS,
  caseDelta,
  caseProblems,
  createStudyEditor,
  isEditable,
  refusedCount,
  touchedProblems,
  type CaseHead,
  type EditorError,
  type ImageRow,
  type Moved,
  type StudyEditor,
} from './store';

/**
 * The details editor of a study (`/studies/:studyUID/details`): the case once,
 * and each image of it, saved as they are typed (store.ts). Done sends what is
 * still waiting and goes back to the study.
 *
 * The route names the study by UID, like the study page, so the public id is
 * asked for in the header's workspace (the one the shell binds DICOMweb to).
 * Only an admin or member of that workspace gets the form; anyone else, or a
 * study that is gone, gets a page that says so.
 */
export function StudyDetailsPage() {
  const { studyUID } = useParams<{ studyUID: string }>();
  const { current, ready } = useSelectedWorkspace();
  // The same questions, under the same keys, as the study page asks.
  const lookup = useQuery({
    queryKey: ['study-lookup', current?.public_id, studyUID],
    queryFn: () => lookupStudy(studyUID!, current?.public_id),
    enabled: !!studyUID && ready,
    retry: false,
  });
  const studyId = lookup.data?.public_id;
  const head = useQuery({
    queryKey: ['study-ref', studyId],
    queryFn: () => getStudyRef<CaseHead>(studyId!),
    enabled: !!studyId,
    retry: false,
  });
  // Images moved to another case: said here even once this study is gone (its last image left).
  const [moved, setMoved] = useState<Moved[]>([]);
  const toStudy = `/studies/${encodeURIComponent(studyUID ?? '')}`;

  const refused = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 403);
  if (lookup.isError || head.isError) {
    return refused(lookup.error) || refused(head.error) ? (
      <CannotEdit title="You can’t edit this study" body={moved.length ? movedSentence(moved) + ' The study has no images left.' : 'It is in another workspace, or it no longer exists.'} />
    ) : (
      <CannotEdit title="This study could not be loaded" body="The server could not be reached. Try again in a moment." />
    );
  }
  if (!head.data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!head.data.editable) {
    return <CannotEdit title="You can’t edit this study" body="Only members and admins of its workspace can change its details. You can still view it." back={toStudy} />;
  }
  return <Editor key={head.data.public_id} head={head.data} toStudy={toStudy} onMoved={setMoved} />;
}

function CannotEdit({ title, body, back }: { title: string; body: string; back?: string }) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="flex max-w-sm flex-col items-center gap-2 text-center">
        <h2 className="text-sm font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{body}</p>
        <Button asChild size="sm" className="mt-2">
          <Link to={back ?? '/'}>{back ? 'Back to the study' : 'Back to studies'}</Link>
        </Button>
      </div>
    </div>
  );
}

const movedSentence = (moved: Moved[]) =>
  moved.length === 1 ? `${moved[0].name} was moved to case ${moved[0].accession}.` : `${moved.length} images were moved to other cases.`;

/** Where an image is, beside its name in the list. */
function stageLabel(r: ImageRow): { text: string; tone: string } {
  switch (r.stage) {
    case 'published':
      return { text: 'imported', tone: 'text-emerald-600 dark:text-emerald-400' };
    case 'needs-details':
      return { text: 'to confirm', tone: 'text-amber-600 dark:text-amber-400' };
    case 'failed':
      return { text: 'could not be imported', tone: 'text-destructive' };
    case 'converting':
    case 'ingesting':
      return { text: `importing ${Math.max(0, Math.min(99, r.percent ?? 0))}%`, tone: 'text-muted-foreground' };
    default:
      return { text: 'uploading', tone: 'text-muted-foreground' };
  }
}

function Editor({ head, toStudy, onMoved }: { head: CaseHead; toStudy: string; onMoved: (m: Moved[]) => void }) {
  const nav = useNavigate();
  const [editor] = useState<StudyEditor>(() => createStudyEditor(head.public_id));
  const s = useStore(editor);
  const [leaving, setLeaving] = useState(false);
  const [held, setHeld] = useState(false);

  useEffect(() => {
    editor.getState().open(head);
    return () => editor.getState().close();
    // Opened once per study (the page is keyed by it); newer heads are taken below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);
  useEffect(() => editor.getState().takeHead(head), [editor, head]);
  useEffect(() => onMoved(s.moved), [s.moved, onMoved]);

  // Said at a field once the person changed it (touchedProblems): stored values are not scolded on opening.
  const problems = useMemo(() => touchedProblems(caseProblems(s.caseForm), s.caseTouched), [s.caseForm, s.caseTouched]);
  const caseErrorOf = (error: EditorError | null) => (...keys: (keyof MetadataDraft)[]) =>
    problems.find((p) => keys.includes(p.field))?.message ?? (error?.field && keys.includes(error.field as keyof MetadataDraft) ? error.message : undefined);
  const row = s.images?.find((r) => r.imageId === s.selectedId) ?? null;
  const refused = refusedCount(s);
  const owed = Object.keys(caseDelta(s.caseForm, s.caseSaved)).length > 0;

  const done = async () => {
    setLeaving(true);
    await editor.getState().flush();
    setLeaving(false);
    if (refusedCount(editor.getState())) {
      setHeld(true);
      return;
    }
    nav(toStudy);
  };

  const status = refused ? (
    <span className="inline-flex items-center gap-1.5 text-xs text-destructive">
      <AlertTriangle className="h-3.5 w-3.5" />
      {refused === 1 ? 'A change is not saved: see the field marked in red' : `${refused} changes are not saved: see the fields marked in red`}
    </span>
  ) : s.pending || leaving ? (
    <span className="text-xs text-muted-foreground">Saving…</span>
  ) : owed ? (
    <span className="text-xs text-muted-foreground">The accession is saved when you leave its field</span>
  ) : s.saved ? (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
      <Check className="h-3.5 w-3.5" />
      Saved
    </span>
  ) : (
    <span className="text-xs text-muted-foreground">Changes are saved as you type</span>
  );

  const imageErrorOf = (r: ImageRow) => {
    const imageProblems = touchedProblems(wireOf(r.draft).problems.filter((p) => !CASE_FIELDS.includes(p.field)), r.touched);
    return (...keys: (keyof MetadataDraft)[]) =>
      imageProblems.find((p) => keys.includes(p.field))?.message ?? (r.error?.field && keys.includes(r.error.field as keyof MetadataDraft) ? r.error.message : undefined);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-3 border-b px-4 py-2">
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm font-semibold">
            <Link to={toStudy} className="hover:underline">
              {[head.accession_number, head.study_id].filter(Boolean).join(' · ') || 'This study'}
            </Link>
          </div>
          <div className="truncate text-xs text-muted-foreground">{head.description || head.study_instance_uid}</div>
        </div>
        {status}
        <Button size="sm" disabled={leaving} onClick={() => void done()}>
          {leaving ? <Loader2 className="animate-spin" /> : <Check />}
          Done
        </Button>
      </div>

      {(s.moved.length > 0 || (held && refused > 0)) && (
        <div className="flex shrink-0 flex-col gap-1.5 px-4 pt-3">
          {s.moved.length > 0 && <Note tone="ok">{movedSentence(s.moved)}</Note>}
          {held && refused > 0 && (
            <p className="flex items-center gap-2 rounded-md bg-destructive/10 px-2.5 py-1.5 text-xs text-destructive">
              <span className="flex-1">Some changes could not be saved. Fix the fields marked in red, or leave without them.</span>
              <Link to={toStudy} className="font-medium underline underline-offset-2">
                Leave without them
              </Link>
            </p>
          )}
        </div>
      )}

      <div className="flex min-h-0 flex-1 gap-4 p-4">
        {/* Left: the case, once for every image of it. */}
        <section className="flex w-[30rem] shrink-0 flex-col rounded-lg border p-4">
          <div className="text-sm font-semibold">Case</div>
          <div className="text-xs text-muted-foreground">Shared by every image of this study. A new accession renames the case, and all its images go with it.</div>
          <div className="mt-3 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto border-t pr-1 pt-3" onBlur={() => void editor.getState().flush(CASE)}>
            <MetadataFields
              part="case"
              draft={s.caseForm.draft}
              kind="wsi"
              set={(p) => editor.getState().setCase(p)}
              errorOf={caseErrorOf(s.caseError)}
              studyExtra={
                <Field label="Diagnosis" className="col-span-2" error={s.caseError?.field === 'diagnosis' ? s.caseError.message : undefined}>
                  <Input value={s.caseForm.diagnosis} maxLength={64} onChange={(e) => editor.getState().setDiagnosis(e.target.value)} />
                </Field>
              }
            />
          </div>
          {s.caseError && !s.caseError.field && <p className="mt-2 text-xs text-destructive">Not saved: {s.caseError.message}</p>}
        </section>

        {/* Right: the images, and the selected one's own details. */}
        <section className="flex min-w-0 flex-1 gap-4 rounded-lg border p-4">
          <div className="flex w-60 shrink-0 flex-col gap-1.5 overflow-y-auto">
            <div className="text-sm font-semibold">
              Images{s.images ? <span className="ml-1.5 font-normal text-muted-foreground">{s.images.length}</span> : null}
            </div>
            {!s.images ? (
              <p className={cn('py-6 text-center text-xs', s.imagesError ? 'text-destructive' : 'text-muted-foreground')}>{s.imagesError ?? 'Loading…'}</p>
            ) : !s.images.length ? (
              <p className="py-6 text-center text-xs text-muted-foreground">This study has no images.</p>
            ) : (
              s.images.map((r) => {
                const { text, tone } = stageLabel(r);
                return (
                  <button
                    key={r.imageId}
                    type="button"
                    onClick={() => editor.getState().select(r.imageId)}
                    className={cn(
                      'flex flex-col gap-0.5 rounded-md border px-2.5 py-1.5 text-left transition-colors',
                      r.imageId === s.selectedId ? 'border-primary bg-accent/50' : 'hover:bg-accent/30',
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2 text-sm">
                      <FileImage className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate" title={r.name}>
                        {r.name}
                      </span>
                    </span>
                    <span className="flex justify-between gap-2 text-[11px]">
                      <span className={tone}>{text}</span>
                      {r.error && <span className="text-destructive">not saved</span>}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-3 border-l pl-4">
            {row ? (
              <>
                <div className="truncate text-sm font-semibold" title={row.name}>
                  {row.name}
                </div>
                {row.stage === 'failed' ? (
                  <Note tone="muted">This image could not be imported{row.stageError ? `: ${row.stageError}` : '.'}</Note>
                ) : !isEditable(row) ? (
                  <Note tone="muted">Still uploading or importing. Its details can be edited once the import finishes.</Note>
                ) : row.partial ? (
                  <Note tone="muted">The server could not send all of this image’s details: a blank field may already hold a value there, and what you enter replaces it.</Note>
                ) : null}
                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto border-t pr-1 pt-3" onBlur={() => void editor.getState().flush(row.imageId)}>
                  <MetadataFields
                    key={row.imageId}
                    part="image"
                    draft={row.draft}
                    kind={row.kind}
                    certain={row.certain}
                    set={(p) => editor.getState().setImage(row.imageId, p)}
                    errorOf={imageErrorOf(row)}
                    disabled={!isEditable(row)}
                  />
                </div>
                {row.error && !row.error.field && <p className="text-xs text-destructive">Not saved: {row.error.message}</p>}

                {isEditable(row) && (
                  <div className="flex flex-col gap-2 border-t pt-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Move to another case</div>
                    <div className="flex items-end gap-2.5">
                      <Field label="Accession #" className="flex-1">
                        <Input value={row.moveTo.accession} onChange={(e) => editor.getState().setMoveTo(row.imageId, { accession: e.target.value })} placeholder="the case this image belongs to" />
                      </Field>
                      <Field label="Accession issuer" className="flex-1">
                        <Input value={row.moveTo.issuer} onChange={(e) => editor.getState().setMoveTo(row.imageId, { issuer: e.target.value })} />
                      </Field>
                      <Button variant="outline" disabled={row.moving || !row.moveTo.accession.trim()} onClick={() => void editor.getState().move(row.imageId)}>
                        {row.moving && <Loader2 className="animate-spin" />}
                        Move
                      </Button>
                    </div>
                    {row.moveError && <p className="text-xs text-destructive">{row.moveError}</p>}
                  </div>
                )}
              </>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Select an image to edit its details.</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
